/**
 * @fileoverview WEB-854 — the row source for the people module: the host
 * behind an event, on every category lexicon since WEB-855.
 *
 * Lives here rather than inside `PeopleModule.tsx` for the same reason
 * `resolveGenericModuleRows` does: `hasModuleContent` in `modules.ts` gates
 * the slot on whether this produces a row, and the gate must not import a
 * React component to find out. One row source, two callers, no second
 * opinion.
 *
 * ## Sources — organizer first, then non-performing contributors
 *
 * 1. **`event.organizer`** — the source-published host. WEB-713 wrote this
 *    file's ancestor against a field that did not exist yet: `EventDetailV2`
 *    carried no organizer at all, so the people slot gated OFF
 *    unconditionally and every Eventbrite community listing rendered an empty
 *    "Organizers" module while its own `/e/` page named the host in plain
 *    sight. WEB-854 closed that end to end — the `/e/` detail-hydration seam
 *    now extracts schema.org `Event.organizer`, `Event.primaryOrganizerName`
 *    / `primaryOrganizerUrl` persist it, and the detail response serves it —
 *    so the field is real and this is no longer speculative.
 * 2. **Artists whose billing marks them as non-performing contributors**, if
 *    the payload ever expresses that distinction. `EventDetailArtist.billing`
 *    is still a closed union of PERFORMER roles
 *    (`'HEADLINER' | 'SUPPORT' | 'OPENER' | null`) with no value meaning
 *    "non-performing contributor", so this source remains inert against
 *    today's real payload — written defensively and type-safely so a future
 *    contract widening the enum is picked up with no change here.
 *
 * **No other source.** In particular, never the venue name (see
 * `__tests__/peopleRows.test.ts`'s explicit negative test), never the source
 * display name, and never the Eventbrite actor's `ticketsBy` field — that is
 * the ticketing PLATFORM, literally the string "Eventbrite", and printing it
 * as the host would be a confident wrong answer dressed as data.
 */

import type { ModuleRow } from '@/components/organisms/EventModuleSection';
import type { EventDetailV2 } from '@/types/eventDetail';

/** The `EventDetailArtist` element type `EventDetailV2.artists` actually carries. */
export type PeopleModuleArtist = NonNullable<EventDetailV2['artists']>[number];

/**
 * The organizer shape this module reads — the generated `EventOrganizer`
 * schema, not a locally invented one. `name` is always a real non-blank name
 * (the server refuses to emit a nameless organizer) and `url` is an
 * Eventbrite-host profile link or null.
 */
export type PeopleModuleOrganizer = NonNullable<EventDetailV2['organizer']>;

/**
 * `EventDetailArtist.billing`'s three real values today, all performer
 * roles. Checked as a raw string (not the narrow `billing` union type) so a
 * future contract widening the enum with a non-performer value is honored
 * without a type change here. Every value the current union can actually
 * produce is in this set, which is what makes the check below structurally
 * always-false in production rather than a guess at an unannounced sentinel.
 */
const PERFORMER_BILLING_ROLES: ReadonlySet<string> = new Set(['HEADLINER', 'SUPPORT', 'OPENER']);

function isNonPerformingContributor(artist: PeopleModuleArtist): boolean {
  const billing = artist.billing;
  return typeof billing === 'string' && !PERFORMER_BILLING_ROLES.has(billing);
}

/**
 * Initials for the row's decorative disc: the first letter of each of the
 * first two words, uppercased. A word contributes nothing when it has no
 * letter at all (e.g. a punctuation-only token), so the disc shows fewer
 * than two letters — or none — rather than a placeholder glyph.
 */
export function initialsFromName(name: string): string {
  const letters = name
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .map((word) => word.match(/\p{L}/u)?.[0])
    .filter((letter): letter is string => Boolean(letter))
    .slice(0, 2);
  return letters.join('').toUpperCase();
}

/** Joins non-empty, trimmed parts with ` · `; `undefined` when nothing survives (self-gates). */
function joinMeta(...parts: ReadonlyArray<string | null | undefined>): string | undefined {
  const cleaned = parts
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter((part) => part.length > 0);
  return cleaned.length > 0 ? cleaned.join(' · ') : undefined;
}

/**
 * The organizer's profile link, when it is one this surface will render.
 *
 * The ingest path already restricts the stored URL to Eventbrite hosts, and
 * the server passes it through verbatim — so this is the second of two
 * checks, not the only one. It exists because the value reaches here as a
 * plain string on a payload, and a link rendered under LOCI's own heading
 * must never be able to carry a `javascript:` or `data:` scheme just because
 * a row was hand-edited or a future source is added upstream.
 */
function organizerHref(url: string | null | undefined): string | undefined {
  if (typeof url !== 'string') return undefined;
  const trimmed = url.trim();
  if (trimmed.length === 0) return undefined;
  return /^https?:\/\//i.test(trimmed) ? trimmed : undefined;
}

function organizerRow(organizer: PeopleModuleOrganizer): ModuleRow | null {
  const name = organizer.name.trim();
  if (name.length === 0) {
    return null;
  }
  // The row's title becomes a real `<a>` only when a resolvable link exists;
  // `ModuleRowList` self-gates to plain text otherwise.
  const href = organizerHref(organizer.url);
  return {
    id: 'organizer',
    visual: { kind: 'initials', text: initialsFromName(name) },
    title: name,
    ...(href === undefined ? {} : { titleHref: href }),
  };
}

function contributorRow(artist: PeopleModuleArtist): ModuleRow | null {
  const name = artist.name.trim();
  if (name.length === 0) {
    return null;
  }
  return {
    id: `artist-${artist.id}`,
    visual: { kind: 'initials', text: initialsFromName(name) },
    title: name,
    // No affiliation exists for an artist at any source (§4.2 degrade case);
    // the raw non-performer billing value is the only role-shaped text
    // available, so it is the whole of the meta line.
    meta: joinMeta(artist.billing),
    // No count exists for a contributor — §4.2: "derivable for an organiser
    // only; omit otherwise."
  };
}

/**
 * Builds the module's rows from `event`, organizer first, then
 * non-performing contributors. Pure; safe to unit test directly with a
 * hand-built `event` that exercises either source. **No other source** — in
 * particular this never reads `event.location.name`.
 */
export function derivePeopleRows(event: EventDetailV2): ModuleRow[] {
  const rows: ModuleRow[] = [];

  const organizer = event.organizer;
  if (organizer) {
    const row = organizerRow(organizer);
    if (row) {
      rows.push(row);
    }
  }

  const artists = Array.isArray(event.artists) ? event.artists : [];
  for (const artist of artists) {
    if (isNonPerformingContributor(artist)) {
      const row = contributorRow(artist);
      if (row) {
        rows.push(row);
      }
    }
  }

  return rows;
}
